import { toPurchaseHistoryPeriod } from "features/purchaseHistory/purchaseHistoryPeriod";
import { useListPurchaseHistory } from "features/purchaseHistory/useListPurchaseHistory";
import { useShippingStatus } from "features/purchases/useShippingStatus";
import { useMemo } from "react";

import { FETCH_STATUS } from "../constants/fetchStatus";
import { INITIAL_LIMIT } from "../constants/initialLimit";
import { convertOrderToCardProps } from "../functions/convertOrderToCardProps";
import { getDispatchStatuses } from "../functions/getDispatchStatuses";
import { getShippingLabelStatus } from "../functions/getShippingLabelStatus";

import type { FetchStatus } from "../types/fetchStatus";
import type { SettlementHistorySource } from "../types/settlementHistorySource";

type Input = {
  /** falseの間は取得を始めない。取得元の切り替え中に二重取得しないため */
  enabled: boolean;
  /** 期間の基準時刻。フィルタ選択時に確定させ、ページをまたいでも同じ期間を送る */
  periodBaseDate: Date;
  selectedFilter: string;
};

type Output = SettlementHistorySource & {
  loadMore: () => void;
};

/**
 * 購入履歴APIから取得する取得元。
 * 期間の絞り込みとページングはサーバーが行い、並び順もAPIの返却順（注文日時の降順）をそのまま使う
 */
export const useSettlementHistoryFromApi = (input: Input): Output => {
  const { enabled, periodBaseDate, selectedFilter } = input;

  const period = useMemo(
    () =>
      toPurchaseHistoryPeriod({
        baseDate: periodBaseDate,
        filter: selectedFilter,
      }),
    [periodBaseDate, selectedFilter]
  );

  const { availableYears, error, hasNextPage, isLoading, loadMore, orders } =
    useListPurchaseHistory({ enabled, pageSize: INITIAL_LIMIT, period });

  const fulfillmentFirestoreIds = orders.flatMap(
    (order) => order.fulfillmentFirestoreIds
  );
  const { shippingStatuses } = useShippingStatus(fulfillmentFirestoreIds);

  // 2ページ目以降の失敗で取得済みの一覧を消さない。エラー表示は1件も出せないときだけ
  const fetchStatus: FetchStatus = isLoading
    ? FETCH_STATUS.LOADING
    : error !== undefined && orders.length === 0
      ? FETCH_STATUS.ERROR
      : FETCH_STATUS.SUCCESS;

  const allHistoryCards = orders.map((order) => {
    const dispatchStatuses = getDispatchStatuses(
      order.fulfillmentFirestoreIds,
      shippingStatuses
    );
    return {
      ...convertOrderToCardProps(order),
      shippingLabelStatus: getShippingLabelStatus(dispatchStatuses),
    };
  });

  return {
    allHistoryCards,
    fetchStatus,
    loadMore,
    settlementYearList: availableYears.map((availableYear) => ({
      numOfSettlements: availableYear.count,
      year: availableYear.year,
    })),
    showLoadMore: hasNextPage,
  };
};
